<template>
  <div class="room-container figma-property-page">
    <!-- Figma-style page header with title and action buttons -->
    <div class="figma-page-header">
      <div class="figma-page-title">
        <span>房产管理</span>
      </div>
      <div class="figma-page-actions">
        <!-- 添加按钮 - 蓝色 -->
        <el-button size="small" class="figma-btn-add">
          <i class="el-icon-plus"></i>添加
        </el-button>
        <!-- 导入按钮 - 橙色 -->
        <el-button size="small" class="figma-btn-import">
          <i class="el-icon-upload2"></i>导入
        </el-button>
        <!-- 模版下载按钮 - 灰色 -->
        <el-button size="small" class="figma-btn-template">
          <i class="el-icon-download"></i>模版下载
        </el-button>
        <!-- 刷新按钮 -->
        <el-button size="small" class="figma-btn-refresh" @click="handleRefreshRoom" title="刷新">
          <i class="el-icon-refresh-right"></i>
        </el-button>
        <!-- 搜索区域 -->
        <div class="figma-header-search">
          <el-input size="small" placeholder="请输入小区信息" class="figma-header-search-input" clearable />
          <el-button size="small" class="figma-btn-header-search">
            <i class="el-icon-search"></i>搜索
          </el-button>
        </div>
      </div>
    </div>

    <!-- Figma-style search bar -->
    <div class="figma-search-bar">
      <div class="figma-search-inner">
        <el-input v-model="roomInfo.conditions.roomId" :placeholder="$t('roomList.roomIdPlaceholder')" size="small"
          prefix-icon="el-icon-search" class="figma-search-input" clearable />
        <el-select v-model="roomInfo.conditions.state" size="small" :placeholder="$t('roomList.selectState')" clearable
          class="figma-search-select">
          <el-option v-for="(item, index) in roomInfo.states" :key="index" :label="item.name"
            :value="item.statusCd"></el-option>
        </el-select>
        <el-select v-model="roomInfo.conditions.roomSubType" size="small" :placeholder="$t('roomList.selectRoomType')"
          clearable class="figma-search-select">
          <el-option v-for="(item, index) in roomInfo.roomSubTypes" :key="index" :label="item.name"
            :value="item.statusCd"></el-option>
        </el-select>
        <el-select v-model="roomInfo.isAll" size="small" @change="changeIsAll" class="figma-search-select">
          <el-option label="当前楼栋单元" value="N"></el-option>
          <el-option label="全部楼栋单元" value="Y"></el-option>
        </el-select>
        <el-button type="primary" size="small" @click="queryRoomMethod" class="figma-btn-search">
          <i class="el-icon-search"></i>{{ $t('roomList.query') }}
        </el-button>
      </div>
    </div>

    <!-- Figma-style action bar -->
    <div class="figma-action-bar">
      <div class="figma-action-left">
        <el-dropdown trigger="click" @command="handleBuildingCommand" v-if="roomInfo.conditions.floorId">
          <el-button size="small" class="figma-btn-outline">
            {{ $t('roomList.editBuilding') }}<i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="edit">{{ $t('roomList.editBuilding') }}</el-dropdown-item>
            <el-dropdown-item command="delete">{{ $t('roomList.deleteBuilding') }}</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <el-dropdown trigger="click" @command="handleUnitCommand" v-if="roomInfo.conditions.unitId">
          <el-button size="small" class="figma-btn-outline">
            {{ $t('roomList.editUnit') }}<i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="edit">{{ $t('roomList.editUnit') }}</el-dropdown-item>
            <el-dropdown-item command="delete">{{ $t('roomList.deleteUnit') }}</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
      <div class="figma-action-right">
        <el-button size="small" @click="openAddRoomFloorModal" class="figma-btn-outline">
          <i class="el-icon-plus"></i>{{ $t('roomList.addBuilding') }}
        </el-button>
        <el-button size="small" @click="openAddRoomUnitModal" v-if="roomInfo.conditions.floorId" class="figma-btn-outline">
          <i class="el-icon-plus"></i>{{ $t('roomList.addUnit') }}
        </el-button>
        <el-button type="primary" size="small" @click="openAddRoom"
          v-if="hasPrivilege('502020082453087940')" class="figma-btn-primary">
          <i class="el-icon-plus"></i>{{ $t('roomList.addRoom') }}
        </el-button>
        <el-button size="small" @click="openImportRoomInfo" class="figma-btn-outline">
          <i class="el-icon-upload2"></i>{{ $t('roomList.importRoom') }}
        </el-button>
      </div>
    </div>

    <!-- Resizable layout -->
    <div class="resizable-layout">
      <!-- Left tree panel -->
      <div class="tree-panel figma-tree-panel" :style="{ width: treeWidth + 'px' }">
        <floor-unit-tree ref="floorUnitTree" @switchFloorUnit="switchFloorUnit" />
      </div>

      <!-- Drag handle -->
      <div class="resize-handle" @mousedown="startResize" @mouseover="showResizeCursor" @mouseleave="hideResizeCursor">
        <div class="resize-line"></div>
      </div>

      <!-- Right content area -->
      <div class="content-panel" :style="{ width: 'calc(100% - ' + (treeWidth + 10) + 'px)' }">
        <div class="figma-table-card">
          <el-table :data="roomInfo.rooms" style="width: 100%" class="figma-table" stripe>
            <el-table-column prop="roomId" :label="$t('roomList.room')" width="120" fixed="left">
              <template slot-scope="scope">
                <span class="figma-room-id">
                  {{ scope.row.floorNum }}-{{ scope.row.unitNum }}-{{ scope.row.roomNum }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="layer" :label="$t('roomList.floor')" width="100" align="center">
              <template slot-scope="scope">
                {{ scope.row.layer }}{{ $t('roomList.layer') }}
              </template>
            </el-table-column>
            <el-table-column prop="ownerName" :label="$t('roomList.owner')" width="150" align="center">
              <template slot-scope="scope">
                <a v-if="scope.row.ownerName" target="_blank"
                  :href="'/#/views/owner/ownerDetail?ownerId=' + scope.row.ownerId" class="figma-link">
                  {{ scope.row.ownerName }}({{ scope.row.link }})
                </a>
                <span v-else class="figma-text-muted">-</span>
              </template>
            </el-table-column>
            <el-table-column prop="roomSubTypeName" :label="$t('roomList.type')" width="120" align="center" />
            <el-table-column :label="$t('roomList.area')" width="150" align="center">
              <template slot-scope="scope">
                {{ scope.row.builtUpArea }}/{{ scope.row.roomArea }} {{ $t('roomList.squareMeter') }}
              </template>
            </el-table-column>
            <el-table-column prop="roomRent" :label="$t('roomList.rent')" width="100" align="center" />
            <el-table-column prop="stateName" :label="$t('roomList.roomStatus')" width="120" align="center">
              <template slot-scope="scope">
                <span class="status-tag" :class="getStateClass(scope.row.state)">{{ scope.row.stateName }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.validity')" width="150" align="center">
              <template slot-scope="scope">
                {{ scope.row.startTime }}<br>~{{ scope.row.endTime }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.ownerMembers')" width="120" align="center">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewOwnerMembers(scope.row)" class="figma-link">
                  {{ scope.row.memberCount || 0 }}
                </a>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.ownerCars')" width="120" align="center">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewOwnerCars(scope.row)" class="figma-link">
                  {{ scope.row.carCount || 0 }}
                </a>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.ownerRooms')" width="120" align="center">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewOwnerRooms(scope.row)" class="figma-link">
                  {{ scope.row.roomCount || 0 }}
                </a>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.complaints')" width="100" align="center">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewComplaints(scope.row)" class="figma-link">
                  {{ scope.row.complaintCount || 0 }}
                </a>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.repairs')" width="100" align="center">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewRepairs(scope.row)" class="figma-link">
                  {{ scope.row.repairCount || 0 }}
                </a>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.roomOweFee')" width="120" align="center">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewRoomOweFees(scope.row)" class="figma-link figma-link-danger">
                  {{ scope.row.roomOweFee || '0.00' }}
                </a>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.ownerOweFee')" width="120" align="center">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewOweFees(scope.row)" class="figma-link figma-link-danger">
                  {{ scope.row.oweFee || '0.00' }}
                </a>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.roomContracts')" width="120" align="center">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewRoomContracts(scope.row)" class="figma-link">
                  {{ scope.row.contractCount || 0 }}
                </a>
              </template>
            </el-table-column>
            <el-table-column v-for="(item, index) in roomInfo.listColumns" :key="index" :label="item" width="120"
              align="center">
              <template slot-scope="scope">
                <span v-if="scope.row.listValues">{{ scope.row.listValues[index] || '' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.operation')" fixed="right" width="200" align="center">
              <template slot-scope="scope">
                <div class="figma-action-group">
                  <el-button v-if="hasPrivilege('502020082493857941')" size="mini" type="text"
                    @click="openEditRoomModel(scope.row)" class="figma-action-btn">
                    {{ $t('roomList.edit') }}
                  </el-button>
                  <el-button v-if="hasPrivilege('502020082417457942')" size="mini" type="text"
                    @click="openDelRoomModel(scope.row)" class="figma-action-btn figma-action-btn-danger">
                    {{ $t('roomList.delete') }}
                  </el-button>
                  <el-button v-if="scope.row.state == '2002' && hasPrivilege('502023021978930012')" size="mini"
                    type="text" @click="toRoomBindOwner(scope.row)" class="figma-action-btn figma-action-btn-primary">
                    {{ $t('roomList.handOver') }}
                  </el-button>
                  <el-button v-if="scope.row.state != '2002' && scope.row.ownerId && hasPrivilege('502023021973760015')"
                    size="mini" type="text" @click="toRoomUnBindOwner(scope.row)" class="figma-action-btn">
                    {{ $t('roomList.checkOut') }}
                  </el-button>
                  <el-button v-if="scope.row.state != '2002'" size="mini" type="text"
                    @click="toSimplifyAcceptance(scope.row)" class="figma-action-btn figma-action-btn-primary">
                    {{ $t('roomList.businessAcceptance') }}
                  </el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>

          <div class="figma-pagination-wrapper">
            <div class="room-detail-desc">
              <div>{{ $t('roomList.roomStatusDesc1') }}</div>
              <div>{{ $t('roomList.roomStatusDesc2') }}</div>
              <div>{{ $t('roomList.roomStatusDesc3') }}</div>
              <div>{{ $t('roomList.rentDesc') }}</div>
            </div>
            <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange"
              :current-page="roomInfo.currentPage" :page-sizes="[10, 20, 30, 40]" :page-size="DEFAULT_ROW"
              layout="total, sizes, prev, pager, next, jumper" :total="roomInfo.total"
              class="figma-pagination" />
          </div>
        </div>
      </div>
    </div>

    <!-- 子组件 -->
    <add-floor ref="addFloor" @handleRefreshTree="floorUnitTree" />
    <edit-floor ref="editFloor" @handleRefreshTree="floorUnitTree" />
    <delete-floor ref="deleteFloor" @handleRefreshTree="floorUnitTree" />
    <add-unit ref="addUnit" @handleRefreshTree="floorUnitTree" />
    <edit-unit ref="editUnit" @handleRefreshTree="floorUnitTree" />
    <delete-unit ref="deleteUnit" @handleRefreshTree="floorUnitTree" />
    <search-floor ref="searchFloor" emit-choose-floor="room" emit-load-data="room" />
    <edit-room ref="editRoom" @handleRefreshRoom="handleRefreshRoom" />
    <delete-room ref="deleteRoom" @handleRefreshRoom="handleRefreshRoom" />
    <import-owner-room ref="importOwnerRoom" />

    <owner-rooms ref="ownerRooms" />
    <owner-members ref="ownerMembers" />
    <owner-cars ref="ownerCars" />
    <owner-complaints ref="ownerComplaints" />
    <owner-repairs ref="ownerRepairs" />
    <owner-owe-fees ref="ownerOweFees" />
    <room-owe-fees ref="roomOweFees" />
    <room-contracts ref="roomContracts" />
  </div>
</template>

<script>
import { getDict } from '@/api/community/communityApi'
import { queryRooms, getFloors, getUnits } from '@/api/room/roomApi'
import FloorUnitTree from '@/components/room/floorUnitTree'
import SearchFloor from '@/components/room/searchFloor'
import EditRoom from '@/components/room/editRoom'
import DeleteRoom from '@/components/room/deleteRoom'
import ImportOwnerRoom from '@/components/room/importOwnerRoom'
import AddFloor from '@/components/room/addFloor'
import EditFloor from '@/components/room/editFloor'
import DeleteFloor from '@/components/room/deleteFloor'
import AddUnit from '@/components/room/addUnit'
import EditUnit from '@/components/room/editUnit'
import DeleteUnit from '@/components/room/deleteUnit'
import OwnerRooms from '@/components/owner/ownerRooms'
import OwnerMembers from '@/components/owner/ownerMembers'
import OwnerCars from '@/components/owner/ownerCars'
import OwnerComplaints from '@/components/owner/ownerComplaints'
import OwnerRepairs from '@/components/owner/ownerRepairs'
import OwnerOweFees from '@/components/owner/ownerOweFees'
import RoomOweFees from '@/components/room/roomOweFees'
import RoomContracts from '@/components/room/roomContracts'

import { getAttrSpecList } from '@/api/dev/attrSpecApi'
// import { getAttrValueList } from '@/api/dev/attrValueApi'

const DEFAULT_PAGE = 1
const DEFAULT_ROW = 10

export default {
  name: 'RoomList',
  components: {
    FloorUnitTree,
    SearchFloor,
    EditRoom,
    DeleteRoom,
    ImportOwnerRoom,
    AddFloor,
    EditFloor,
    DeleteFloor,
    AddUnit,
    EditUnit,
    DeleteUnit,
    OwnerRooms,
    OwnerMembers,
    OwnerCars,
    OwnerComplaints,
    OwnerRepairs,
    OwnerOweFees,
    RoomOweFees,
    RoomContracts
  },
  data() {
    return {
      DEFAULT_PAGE,
      DEFAULT_ROW,
      callbackListener: 'room',
      roomInfo: {
        rooms: [],
        total: 0,
        records: 1,
        floorId: '',
        unitId: '',
        states: [],
        roomNum: '',
        isAll: 'N',
        moreCondition: false,
        conditions: {
          floorId: '',
          floorName: '',
          unitId: '',
          roomNum: '',
          roomId: '',
          state: '',
          section: '',
          roomType: '1010301',
          roomSubType: '',
          flag: ''
        },
        currentPage: DEFAULT_PAGE,
        listColumns: [],
        roomSubTypes: []
      },
      // 新增：拖拽相关数据
      treeWidth: 300, // 默认树形组件宽度
      minTreeWidth: 200, // 最小宽度
      maxTreeWidth: 600, // 最大宽度
      isResizing: false,
      startX: 0,
      startWidth: 0
    }
  },
  mounted() {
    this.initData()
    // 恢复保存的宽度
    this.restoreTreeWidth()
    // 添加全局鼠标事件监听
    document.addEventListener('mousemove', this.onMouseMove)
    document.addEventListener('mouseup', this.onMouseUp)
  },
  beforeDestroy() {
    // 移除全局事件监听
    document.removeEventListener('mousemove', this.onMouseMove)
    document.removeEventListener('mouseup', this.onMouseUp)
  },
  methods: {
    async initData() {
      this.roomInfo.conditions.floorId = this.$route.query.floorId || ''
      this.roomInfo.conditions.floorName = this.$route.query.floorName || ''

      try {
        // 获取房屋状态字典
        const stateData = await getDict('building_room', 'state')
        this.roomInfo.states = stateData

        // 获取房屋类型字典
        const roomTypeData = await getDict('building_room', 'room_sub_type')
        this.roomInfo.roomSubTypes = roomTypeData

        this.listRoom(DEFAULT_PAGE, DEFAULT_ROW)
      } catch (error) {
        console.error('初始化数据失败:', error)
      }
    },
    handleRefreshRoom() {
      this.listRoom(this.roomInfo.currentPage, DEFAULT_ROW)
    },
    async listRoom(page, row) {
      this.roomInfo.conditions.page = page
      this.roomInfo.conditions.row = row
      this.roomInfo.conditions.communityId = this.getCommunityId()

      const params = { ...this.roomInfo.conditions }

      // 处理房间号格式
      const allNum = params.roomId || ''
      const allNums = allNum.split('-')
      if (allNums.length >= 3) {
        params.floorNum = allNums[0].trim()
        params.unitNum = allNums[1].trim()
        params.roomNum = allNums.slice(2).join('-').trim()
        params.roomId = ''
        params.unitId = ''
        params.floorId = ''
      }

      try {
        const res = await queryRooms(params)
        this.roomInfo.total = res.total
        this.roomInfo.records = res.records
        this.roomInfo.rooms = this.normalizeRoomRows(res.rooms)
        await this.dealRoomAttr(this.roomInfo.rooms)
      } catch (error) {
        console.error('获取房间列表失败:', error)
        this.$message.error('获取房间列表失败')
      }
    },

    normalizeRoomRows(rooms) {
      return (rooms || []).map(room => ({
        ...room,
        roomSubTypeName: this.getRoomSubTypeName(room)
      }))
    },

    getRoomSubTypeName(room) {
      if (room.roomSubTypeName) {
        return room.roomSubTypeName
      }
      const roomSubType = room.roomSubType || room.room_sub_type
      const match = this.roomInfo.roomSubTypes.find(item => String(item.statusCd) === String(roomSubType))
      return match ? match.name : '-'
    },

    async dealRoomAttr(rooms) {
      if (!rooms || !rooms.length) return

      // 获取表格列配置后再处理每个房屋的属性值
      await this.getColumns()
      rooms.forEach(item => {
        this.getColumnsValue(item)
      })
    },

    getColumnsValue(room) {
      room.listValues = []
      if (!room.roomAttrDto || !room.roomAttrDto.length) {
        this.roomInfo.listColumns.forEach(() => {
          room.listValues.push('')
        })
        return
      }

      this.roomInfo.listColumns.forEach(column => {
        const attr = room.roomAttrDto.find(dto => dto.specName === column)
        room.listValues.push(attr ? attr.valueName : '')
      })
    },

    async getColumns() {
      try {
        const { data } = await getAttrSpecList({
          page: 1,
          row: 100,
          tableName: 'building_room_attr'
        })
        this.roomInfo.listColumns = data
          .filter(item => item.listShow === 'Y')
          .map(item => item.specName)
      } catch (error) {
        console.error('获取房屋属性失败:', error)
      }
    },

    switchFloorUnit(data) {
      this.roomInfo.conditions.floorId = data.floorId
      this.roomInfo.conditions.unitId = data.unitId
      this.roomInfo.conditions.flag = 1
      this.roomInfo.isAll = 'N'
      this.listRoom(DEFAULT_PAGE, DEFAULT_ROW)
    },

    handleSizeChange(val) {
      this.listRoom(this.roomInfo.currentPage, val)
    },

    handleCurrentChange(val) {
      this.roomInfo.currentPage = val
      this.listRoom(val, DEFAULT_ROW)
    },


    handleResize() {
      this.$forceUpdate()
    },

    floorUnitTree(param) {
      this.$refs.floorUnitTree.refreshTree(param)
    },
    // 按钮操作相关方法
    async openAddRoom() {
      let _floorId = this.roomInfo.conditions.floorId;
      if(this.roomInfo.conditions.unitId){
        const unitData = await this.loadUnits(this.roomInfo.conditions.unitId)
        _floorId = unitData.floorId;
      }
      this.$router.push('/views/room/addRoomView?floorId='+_floorId+"&unitId="+this.roomInfo.conditions.unitId)
    },

    openEditRoomModel(room) {
      this.$refs.editRoom.open(room)
    },

    openDelRoomModel(room) {
      this.$refs.deleteRoom.open(room)
    },

    queryRoomMethod() {
      this.roomInfo.conditions.flag = 0
      this.listRoom(DEFAULT_PAGE, DEFAULT_ROW)
    },

    resetRoomInfo() {
      this.roomInfo.conditions = {
        floorId: '',
        floorName: '',
        unitId: '',
        roomNum: '',
        roomId: '',
        state: '',
        section: '',
        roomType: '1010301',
        roomSubType: '',
        flag: ''
      }
      this.listRoom(DEFAULT_PAGE, DEFAULT_ROW)
    },

    openImportRoomInfo() {
      this.$refs.importOwnerRoom.open()
    },

    openAddRoomFloorModal() {
      this.$refs.addFloor.open()
    },

    async openUpdateRoomFloorModal() {
      if (!this.roomInfo.conditions.floorId) {
        this.$message.warning('请先选择楼栋')
        return
      }

      try {
        const floorData = await this.loadFloor(this.roomInfo.conditions.floorId)
        this.$refs.editFloor.open(floorData)
      } catch (error) {
        console.error('加载楼栋信息失败:', error)
      }
    },

    async openDeleteRoomFloorModal() {
      if (!this.roomInfo.conditions.floorId) {
        this.$message.warning('请先选择楼栋')
        return
      }

      try {
        const floorData = await this.loadFloor(this.roomInfo.conditions.floorId)
        this.$refs.deleteFloor.open(floorData)
      } catch (error) {
        console.error('加载楼栋信息失败:', error)
      }
    },

    openAddRoomUnitModal() {
      if (!this.roomInfo.conditions.floorId) {
        this.$message.warning('请先选择楼栋')
        return
      }
      this.$refs.addUnit.open({ floorId: this.roomInfo.conditions.floorId })
    },

    async openUpdateRoomUnitModal() {
      if (!this.roomInfo.conditions.unitId) {
        this.$message.warning('请先选择单元')
        return
      }

      try {
        const unitData = await this.loadUnits(this.roomInfo.conditions.unitId)
        this.$refs.editUnit.open(unitData)
      } catch (error) {
        console.error('加载单元信息失败:', error)
      }
    },

    async openDeleteRoomUnitModal() {
      if (!this.roomInfo.conditions.unitId) {
        this.$message.warning('请先选择单元')
        return
      }

      try {
        const unitData = await this.loadUnits(this.roomInfo.conditions.unitId)
        this.$refs.deleteUnit.open(unitData)
      } catch (error) {
        console.error('加载单元信息失败:', error)
      }
    },

    changeIsAll() {
      if (this.roomInfo.isAll === 'Y') {
        this.roomInfo.conditions.floorId = ''
        this.roomInfo.conditions.unitId = ''
      }
      this.listRoom(this.roomInfo.currentPage, DEFAULT_ROW)
    },

    toSimplifyAcceptance(room) {
      this.$router.push('/pages/property/simplifyAcceptance?tab=业务受理&searchType=1&searchValue=' + `${room.floorNum}-${room.unitNum}-${room.roomNum}`)
    },

    viewOwnerRooms(room) {
      this.$refs.ownerRooms.open(room)
    },

    viewOwnerMembers(room) {
      this.$refs.ownerMembers.open(room.ownerId)
    },

    viewOwnerCars(room) {
      this.$refs.ownerCars.open(room.ownerId)
    },

    viewComplaints(room) {
      this.$refs.ownerComplaints.open(room.ownerId)
    },

    viewRepairs(room) {
      this.$refs.ownerRepairs.open(room.ownerId)
    },

    viewOweFees(room) {
      this.$refs.ownerOweFees.open(room.ownerId)
    },

    viewRoomOweFees(room) {
      this.$refs.roomOweFees.open(room.roomId)
    },

    viewRoomContracts(room) {
      this.$refs.roomContracts.open({ roomId: room.roomId })
    },

    toRoomBindOwner(room) {
      this.$router.push(`/views/owner/roomBindOwner?roomId=${room.roomId}`)
    },

    toRoomUnBindOwner(room) {
      this.$router.push(`/views/owner/deleteOwnerRoom?ownerId=${room.ownerId}`)
    },

    showMarkdown() {
      this.$router.push('/pages/property/room')
    },

    handleBuildingCommand(command) {
      if (command === 'edit') {
        this.openUpdateRoomFloorModal()
      } else if (command === 'delete') {
        this.openDeleteRoomFloorModal()
      }
    },

    handleUnitCommand(command) {
      if (command === 'edit') {
        this.openUpdateRoomUnitModal()
      } else if (command === 'delete') {
        this.openDeleteRoomUnitModal()
      }
    },

    getStateClass(state) {
      const map = {
        '2002': 'status-tag--unsold',
        '2003': 'status-tag--handed',
        '2004': 'status-tag--decorating',
        '2005': 'status-tag--occupied',
        '2006': 'status-tag--rented',
        '2007': 'status-tag--sold',
        '2008': 'status-tag--idle',
      }
      return map[state] || 'status-tag--default'
    },

    async loadFloor(floorId) {
      const params = {
        page: 1,
        row: 1,
        communityId: this.getCommunityId(),
        floorId: floorId
      }
      const res = await getFloors(params)
      return res.apiFloorDataVoList[0]
    },

    async loadUnits(unitId) {
      const params = {
        unitId: unitId,
        communityId: this.getCommunityId(),
      }
      const res = await getUnits(params)
      return res[0]
    },
     // 新增：恢复保存的树形组件宽度
     restoreTreeWidth() {
      const savedWidth = localStorage.getItem('roomListTreeWidth')
      if (savedWidth) {
        const width = parseInt(savedWidth)
        if (width >= this.minTreeWidth && width <= this.maxTreeWidth) {
          this.treeWidth = width
        }
      }
    },
    // 新增：保存树形组件宽度
    saveTreeWidth() {
      localStorage.setItem('roomListTreeWidth', this.treeWidth.toString())
    },
    // 新增：开始拖拽调整大小
    startResize(event) {
      event.preventDefault()
      this.isResizing = true
      this.startX = event.clientX
      this.startWidth = this.treeWidth
      
      // 添加拖拽时的样式
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    // 新增：鼠标移动处理
    onMouseMove(event) {
      if (!this.isResizing) return
      
      const deltaX = event.clientX - this.startX
      const newWidth = this.startWidth + deltaX
      
      // 限制宽度范围
      if (newWidth >= this.minTreeWidth && newWidth <= this.maxTreeWidth) {
        this.treeWidth = newWidth
      }
    },
    // 新增：鼠标释放处理
    onMouseUp() {
      if (this.isResizing) {
        this.isResizing = false
        this.saveTreeWidth()
        
        // 恢复鼠标样式
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    },
    // 新增：显示调整大小光标
    showResizeCursor() {
      if (!this.isResizing) {
        document.body.style.cursor = 'col-resize'
      }
    },
    // 新增：隐藏调整大小光标
    hideResizeCursor() {
      if (!this.isResizing) {
        document.body.style.cursor = ''
      }
    },
  }
}
</script>
<style scoped>
.room-container {
  padding: 0;
  height: calc(100vh - 120px);
  overflow: hidden;
  background: var(--figma-bg, #EBF2FA);
  display: flex;
  flex-direction: column;
}

/* ===== Figma-style page header ===== */
.figma-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #FFFFFF;
  border-bottom: 1px solid var(--figma-border, #DCE6F5);
}

.figma-page-title {
  font-size: 18px;
  font-weight: 600;
  color: #1D2129;
}

.figma-page-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 添加按钮 - 蓝色 */
.figma-btn-add {
  background: #0E76FF !important;
  border: none !important;
  border-radius: 10px !important;
  color: #FFFFFF !important;
  font-weight: 500;
}

.figma-btn-add:hover {
  background: #0A6AE5 !important;
}

/* 导入按钮 - 橙色 */
.figma-btn-import {
  background: #FF6900 !important;
  border: none !important;
  border-radius: 10px !important;
  color: #FFFFFF !important;
  font-weight: 500;
}

.figma-btn-import:hover {
  background: #E55D00 !important;
}

/* 模版下载按钮 - 灰色 */
.figma-btn-template {
  background: #6A7282 !important;
  border: none !important;
  border-radius: 10px !important;
  color: #FFFFFF !important;
  font-weight: 500;
}

.figma-btn-template:hover {
  background: #5A6169 !important;
}

/* 头部搜索区域 */
.figma-header-search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 20px;
}

.figma-header-search-input {
  width: 256px;
}

.figma-header-search-input >>> .el-input__inner {
  border-radius: 10px;
  border: 1px solid var(--figma-border, #DCE6F5);
  height: 38px;
}

/* 头部搜索按钮 - 蓝色描边 */
.figma-btn-header-search {
  border-radius: 10px !important;
  border: 1px solid #0E76FF !important;
  color: #0E76FF !important;
  background: transparent !important;
  font-weight: 500;
}

.figma-btn-header-search:hover {
  background: rgba(14, 118, 255, 0.08) !important;
}

/* ===== Figma-style search bar ===== */
.figma-search-bar {
  background: #FFFFFF;
  padding: 16px 20px;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 1px 4px rgba(14, 118, 255, 0.06);
}

.figma-search-inner {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.figma-search-input {
  width: 240px;
}

.figma-search-input >>> .el-input__inner {
  border-radius: 10px;
  border: 1px solid var(--figma-border, #DCE6F5);
  height: 38px;
}

.figma-search-select {
  width: 160px;
}

.figma-search-select >>> .el-input__inner {
  border-radius: 10px;
  border: 1px solid var(--figma-border, #DCE6F5);
  height: 38px;
}

.figma-btn-search {
  background: linear-gradient(135deg, #0E76FF, #4D9FFF) !important;
  border: none !important;
  border-radius: 10px !important;
  height: 38px;
  padding: 0 24px;
  font-weight: 500;
}

/* ===== Figma-style action bar ===== */
.figma-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: #FFFFFF;
  margin-top: 1px;
  box-shadow: 0 1px 4px rgba(14, 118, 255, 0.06);
}

.figma-action-left,
.figma-action-right {
  display: flex;
  gap: 8px;
  align-items: center;
}

.figma-btn-primary {
  background: linear-gradient(135deg, #0E76FF, #4D9FFF) !important;
  border: none !important;
  border-radius: 10px !important;
  font-weight: 500;
}

.figma-btn-outline {
  border-radius: 10px !important;
  border: 1px solid var(--figma-border, #DCE6F5) !important;
  color: var(--figma-primary, #0E76FF) !important;
  background: #FFFFFF !important;
  font-weight: 500;
}

.figma-btn-outline:hover {
  border-color: var(--figma-primary, #0E76FF) !important;
  background: rgba(14, 118, 255, 0.04) !important;
}

/* ===== Resizable layout ===== */
.resizable-layout {
  display: flex;
  height: calc(100% - 120px);
  position: relative;
  padding: 16px;
  gap: 0;
}

.figma-tree-panel {
  background: #FFFFFF;
  border-radius: 12px;
  padding: 16px;
  overflow-y: auto;
  border: 1px solid var(--figma-border, #DCE6F5);
  box-shadow: 0 2px 12px rgba(14, 118, 255, 0.08);
  min-height: 400px;
}

.content-panel {
  overflow-y: auto;
}

.resize-handle {
  width: 10px;
  cursor: col-resize;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.resize-handle:hover .resize-line {
  background-color: var(--figma-primary, #0E76FF);
}

.resize-line {
  width: 2px;
  height: 30px;
  background-color: var(--figma-border, #DCE6F5);
  border-radius: 1px;
  transition: background-color 0.2s;
}

/* ===== Figma-style table card ===== */
.figma-table-card {
  background: #FFFFFF;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(14, 118, 255, 0.08);
  border: 1px solid var(--figma-border, #DCE6F5);
}

.figma-table >>> .el-table__header th {
  background-color: #EBF2FA !important;
  color: #1F1F1F;
  font-weight: 500;
  font-size: 13px;
  border-bottom: 1px solid var(--figma-border, #DCE6F5);
}

.figma-table >>> .el-table__body td {
  border-bottom: 1px solid #F0F4FA;
  font-size: 13px;
  color: #333;
}

.figma-table >>> .el-table__row--striped td {
  background-color: #F8FBFF !important;
}

.figma-table >>> .el-table__row:hover > td {
  background-color: #EBF2FA !important;
}

.figma-table >>> .el-table--border .el-table__cell {
  border-right: 1px solid #F0F4FA;
}

.figma-table >>> .el-table--border::after,
.figma-table >>> .el-table--group::after,
.figma-table >>> .el-table--border::before {
  background-color: var(--figma-border, #DCE6F5);
}

/* ===== Room ID style ===== */
.figma-room-id {
  font-weight: 600;
  color: var(--figma-primary, #0E76FF);
}

/* ===== Link styles ===== */
.figma-link {
  color: var(--figma-primary, #0E76FF);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
}

.figma-link:hover {
  color: #4D9FFF;
  text-decoration: underline;
}

.figma-link-danger {
  color: #FB2C36;
}

.figma-link-danger:hover {
  color: #FF5C5C;
}

.figma-text-muted {
  color: #C0C4CC;
}

/* ===== Status tag (capsule) ===== */
.status-tag {
  display: inline-block;
  padding: 2px 12px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
}

.status-tag--unsold {
  background: rgba(144, 147, 153, 0.1);
  color: #909399;
}

.status-tag--handed {
  background: rgba(14, 118, 255, 0.1);
  color: #0E76FF;
}

.status-tag--decorating {
  background: rgba(230, 162, 60, 0.1);
  color: #E6A23C;
}

.status-tag--occupied {
  background: rgba(103, 194, 58, 0.1);
  color: #67C23A;
}

.status-tag--rented {
  background: rgba(14, 118, 255, 0.1);
  color: #0E76FF;
}

.status-tag--sold {
  background: rgba(103, 194, 58, 0.1);
  color: #67C23A;
}

.status-tag--idle {
  background: rgba(144, 147, 153, 0.1);
  color: #909399;
}

.status-tag--default {
  background: rgba(14, 118, 255, 0.1);
  color: #0E76FF;
}

/* ===== Action buttons ===== */
.figma-action-group {
  display: flex;
  gap: 4px;
  justify-content: center;
  flex-wrap: wrap;
}

.figma-action-btn {
  padding: 4px 8px !important;
  font-size: 13px !important;
  color: var(--figma-primary, #0E76FF) !important;
}

.figma-action-btn:hover {
  background: rgba(14, 118, 255, 0.06) !important;
}

.figma-action-btn-danger {
  color: #FB2C36 !important;
}

.figma-action-btn-danger:hover {
  background: rgba(251, 44, 54, 0.06) !important;
}

.figma-action-btn-primary {
  color: var(--figma-primary, #0E76FF) !important;
  font-weight: 500;
}

/* ===== Pagination ===== */
.figma-pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #F0F4FA;
}

.room-detail-desc {
  text-align: left;
  font-size: 12px;
  color: #8C9BB4;
  line-height: 1.6;
  max-width: 50%;
}

.figma-pagination >>> .el-pagination.is-background .el-pager li:not(.disabled).active {
  background: linear-gradient(135deg, #0E76FF, #4D9FFF);
  border-radius: 8px;
}

.figma-pagination >>> .el-pagination.is-background .el-pager li {
  border-radius: 8px;
}

.figma-pagination >>> .btn-prev,
.figma-pagination >>> .btn-next {
  border-radius: 8px !important;
}

/* ===== Resizing states ===== */
.resizable-layout.resizing {
  cursor: col-resize;
}

.resizable-layout.resizing .tree-panel,
.resizable-layout.resizing .content-panel {
  pointer-events: none;
}

.resizable-layout.resizing .resize-handle {
  pointer-events: auto;
}

.tree-panel * {
  user-select: none;
}

.resizable-layout.resizing * {
  user-select: none !important;
}
</style>
